// The customer's other tickets on the helpdesk, newest first; each opens in the helpdesk itself.

import { useState } from 'react';
import { ExternalLink, History, LoaderCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCustomerTickets } from '@/hooks/use-sentinel-workspace';
import { CollapseBody, CollapseHeader } from './collapse-toggle';
import { formatTimestamp } from './tool-call-format';

/** The similar-tickets card's rows: seven before it scrolls, at the same height and gap. */
const VISIBLE_ROWS = 7;
const ROW_HEIGHT_PX = 34;
const ROW_GAP_PX = 2;

/** Open and closed are what every helpdesk reports; anything else shows as the helpdesk named it. */
function StatusChip({ status }: { status: string | null }) {
  if (!status) return null;
  const open = status.toLowerCase() === 'open';
  return (
    <span
      className={cn(
        'shrink-0 rounded-2xl px-2.5 py-1 text-[12px] leading-4 capitalize',
        open ? 'bg-green-100 text-green-500' : 'bg-gray-100 text-gray-800',
      )}
    >
      {status}
    </span>
  );
}

/** Collapsed until opened: the helpdesk is asked once, the first time an agent opens it. */
export function CustomerTicketsCard({ ticketId }: { ticketId: number }) {
  const [open, setOpen] = useState(false);
  const [opened, setOpened] = useState(false);
  const { tickets, loading, hasMore } = useCustomerTickets(opened ? ticketId : null);
  const scrolls = tickets.length > VISIBLE_ROWS;

  return (
    <div className="flex w-full flex-col border-l-4 border-solid border-transparent bg-white">
      <div className={cn('transition-[padding] duration-300 ease-in-out motion-reduce:transition-none flex w-full flex-col px-5 pt-4', open ? 'pb-2' : 'pb-4')}>
        <CollapseHeader
          open={open}
          label="Customer Tickets"
          variant="chevron"
          onToggle={() => {
            setOpen((prev) => !prev);
            setOpened(true);
          }}
          className="h-5"
        >
          <div className="flex min-w-0 items-center gap-2">
            <History className="size-5 shrink-0 text-text-body" />
            <p className="truncate text-[14px] font-semibold leading-[1.1] text-text-body">Customer Tickets</p>
            {open && !loading && (
              <span className="text-[12px] leading-4 text-gray-700">
                {tickets.length}
                {hasMore ? '+' : ''}
              </span>
            )}
          </div>
        </CollapseHeader>
      </div>

      <CollapseBody open={open}>
        <div className="flex w-full flex-col px-3 pb-3">
          {loading ? (
            <div className="flex items-center justify-center gap-2.5 px-4 py-3">
              <LoaderCircle className="size-[14px] animate-spin text-gray-900" />
              <p className="text-[13px] leading-[18px] text-text-body">Loading the customer's tickets</p>
            </div>
          ) : tickets.length === 0 ? (
            <p className="px-2 py-2 text-[13px] leading-[18px] text-gray-700">No other tickets from this customer</p>
          ) : (
            <div
              style={{
                gap: ROW_GAP_PX,
                ...(scrolls ? { maxHeight: VISIBLE_ROWS * ROW_HEIGHT_PX + (VISIBLE_ROWS - 1) * ROW_GAP_PX } : {}),
              }}
              className={cn('flex w-full flex-col', scrolls && 'overflow-y-auto [scrollbar-gutter:stable]')}
            >
              {tickets.map((ticket) => {
                const when = ticket.createdAt ? formatTimestamp(ticket.createdAt.split('T')[0]!) : null;
                const row = (
                  <>
                    <span className="flex min-w-0 items-center gap-1.5">
                      <span className="shrink-0 text-[13px] leading-[18px] text-text-body">#{ticket.id}</span>
                      <span className="truncate text-[13px] leading-[18px] text-gray-800">
                        {ticket.subject || 'No subject'}
                      </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-1.5">
                      <StatusChip status={ticket.status} />
                      {ticket.url && <ExternalLink className="size-[14px] text-gray-700" />}
                    </span>
                  </>
                );
                const className =
                  'flex w-full shrink-0 items-center justify-between gap-2 rounded-lg px-2 text-left transition-colors';
                return ticket.url ? (
                  <a
                    key={ticket.id}
                    href={ticket.url}
                    target="_blank"
                    rel="noreferrer"
                    title={when ? `Opened ${when}` : undefined}
                    style={{ height: ROW_HEIGHT_PX }}
                    className={cn(className, 'cursor-pointer hover:bg-gray-100')}
                  >
                    {row}
                  </a>
                ) : (
                  <div key={ticket.id} title={when ? `Opened ${when}` : undefined} style={{ height: ROW_HEIGHT_PX }} className={className}>
                    {row}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CollapseBody>
    </div>
  );
}
